import React, {useState, useEffect, forwardRef} from 'react';
import {Box, Button, Typography, Slide, TextField, IconButton, Badge, Snackbar, Alert} from '@mui/material';
import ChatIcon from '@mui/icons-material/Chat';
import CloseIcon from '@mui/icons-material/Close';
import { keyframes } from '@mui/system';
import { apiService } from '../../services/api';

const pulseAnimation = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(91, 192, 222, 0.7);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(91, 192, 222, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(91, 192, 222, 0);
  }
`;

const SlideTransition = forwardRef(function Transition(props, ref) {
    const { children, ...other } = props;
    return (
        <Slide direction="down" ref={ref} {...other}>
            {children}
        </Slide>
    );
});

const ConsultationPopup = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [showBadge, setShowBadge] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        message: ''
    });
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success'
    });

    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        const timer = setTimeout(() => {
            setShowBadge(true);
        }, 5000); // Show badge after 5 seconds

        return () => clearTimeout(timer);
    }, []);

    const togglePopup = () => {
        setIsOpen(!isOpen);
        setShowBadge(false);
    };

    const handleInputChange = (event) => {
        const { name, value } = event.target;
        setFormData(prevData => ({
            ...prevData,
            [name]: value
        }));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setIsSubmitting(true);
        try {
            await apiService.submitContactForm(formData);
            setSnackbar({
                open: true,
                message: 'Message sent successfully!',
                severity: 'success'
            });
            setIsOpen(false); // Close popup
        } catch (error) {
            console.error('Error submitting form:', error);
            setSnackbar({
                open: true,
                message: 'Failed to send message. Please try again.',
                severity: 'error'
            });
        } finally {
            setIsSubmitting(false);
            setFormData({ name: '', email: '', message: '' });
        }
    };
    const handleCloseSnackbar = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setSnackbar(prev => ({ ...prev, open: false }));
    };

    return (
        <>
            {!isOpen && (
                <Badge
                    color="error"
                    variant="dot"
                    invisible={!showBadge}
                    sx={{
                        position: 'fixed',
                        right: 20,
                        bottom: 20,
                    }}
                >
                    <IconButton
                        onClick={togglePopup}
                        sx={{
                            width: 64,
                            height: 64,
                            bgcolor: '#5BC0DE',
                            color: 'white',
                            '&:hover': {
                                bgcolor: '#46b8da',
                            },
                            animation: `${pulseAnimation} 2s infinite`,
                        }}
                    >
                        <ChatIcon sx={{ fontSize: 32 }} />
                    </IconButton>
                </Badge>
            )}
            <Slide direction="up" in={isOpen} mountOnEnter unmountOnExit>
                <Box
                    sx={{
                        position: 'fixed',
                        right: 20,
                        bottom: 20,
                        width: 300,
                        bgcolor: 'white',
                        borderRadius: 2,
                        boxShadow: 3,
                        overflow: 'hidden',
                    }}
                >
                    <Box sx={{ bgcolor: '#5BC0DE', p: 2, position: 'relative' }}>
                        <Typography variant="h6" sx={{ color: 'white', pr: 4 }}>
                            Please fill out the form below and we will get back to you as soon as possible.
                        </Typography>
                        <IconButton
                            onClick={togglePopup}
                            sx={{
                                position: 'absolute',
                                right: 8,
                                top: 8,
                                color: 'white',
                                padding: '4px',
                            }}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>
                    <Box component="form" onSubmit={handleSubmit} sx={{ p: 2 }}>
                        <TextField
                            fullWidth
                            label="Name"
                            name="name"
                            variant="outlined"
                            margin="normal"
                            required
                            value={formData.name}
                            onChange={handleInputChange}
                        />
                        <TextField
                            fullWidth
                            label="Email"
                            name="email"
                            variant="outlined"
                            margin="normal"
                            required
                            type="email"
                            value={formData.email}
                            onChange={handleInputChange}
                        />
                        <TextField
                            fullWidth
                            label="Message"
                            name="message"
                            variant="outlined"
                            margin="normal"
                            required
                            multiline
                            rows={4}
                            value={formData.message}
                            onChange={handleInputChange}
                        />
                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            sx={{
                                mt: 2,
                                bgcolor: '#5BC0DE',
                                '&:hover': {
                                    bgcolor: '#46b8da',
                                },
                            }}
                        >
                            {isSubmitting ? 'Submitting...' : 'Submit'}
                        </Button>
                    </Box>
                </Box>
            </Slide>
            <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'top', horizontal: 'center' }} TransitionComponent={SlideTransition}>
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }} variant="filled"
                       elevation={6}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    );
};

export default ConsultationPopup;